// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { Account } from './account';
import { AccountAddress } from './account-address';
import { AccountAdmin } from './account-admin';
import { AccountAdminGroup } from './account-admin-group';
import { AccountIssuanceUserLog } from './account-issuance-user-log';
import { AccountProcurementCard } from './account-procurement-card';
import { AccountPurchaseOrder } from './account-purchase-order';
import { AccountPurchaseOrderLog } from './account-purchase-order-log';
import { AccountUser } from './account-user';
import { CancellationRequest } from './cancellation-request';
import { JobOrderBudgetLog } from './job-order-budget-log';
import { JobOrderDetail } from './job-order-detail';
import { JobOrderHistDetail } from './job-order-hist-detail';
import { JobOrderNote } from './job-order-note';
import { JobOrderOveragePayment } from './job-order-overage-payment';
import { JobOrderStatus } from './job-order-status';
import { JobOrderType } from './job-order-type';
import { Manifest } from './manifest';
import { Program } from './program';
import { ProgramAllowanceUserLog } from './program-allowance-user-log';
import { ProgramIssuanceUserLog } from './program-issuance-user-log';
import { ProgramUserGroup } from './program-user-group';
import { ProximityUser } from './proximity-user';
import { ReturnRequest } from './return-request';
import { ShipFrequency } from './ship-frequency';
import { JobOrderStatusEnum } from './enums';
import { JobOrderTypeEnum } from './enums';
import { ShipFrequencyEnum } from './enums';

/// <code-import> Place custom imports between <code-import> tags
import { JobOrderDetailPriceInfo } from '@core';
import * as _ from 'lodash';
import { ActiveStatus } from './active-status';
import { InvoiceHeaderDTO } from './invoice-from-supplier-dto';
/// </code-import>

/// <module-code> Place module level code between <module-code> tags
/**
 * Committed to the supplier pipeline; the ordering user can no longer work on the order.
 * Replaces the ordinal test `jobOrderStatusId >= JobOrderStatusEnum.AwaitingPlacement`.
 *
 * JobOrderStatusEnum is NOT a reliable ordering — OnHold = 5 already sits between
 * Placed = 4 and Closed = 6, and any status appended later lands numerically above
 * Placed regardless of where it belongs in the lifecycle. An explicit set says what it
 * means and does not silently absorb new members.
 *
 * Mirrors JobOrderStatus.AtOrBeyondPlacementStatuses on the server (Proximity.Model/JobOrder.cs),
 * where the inherited quirks (Cancelled and OnHold are both included) are documented.
 */
export const AT_OR_BEYOND_PLACEMENT_STATUSES: JobOrderStatusEnum[] = [
  JobOrderStatusEnum.AwaitingPlacement,
  JobOrderStatusEnum.Placed,
  JobOrderStatusEnum.OnHold,
  JobOrderStatusEnum.Closed,
  JobOrderStatusEnum.Cancelled,
];

/**
 * In flight but not yet with a supplier. An order in one of these states loses every UI
 * surface if its ProgramUserGroup membership disappears — the user's Unplaced Orders tab
 * filters by currently-accessible programs, so the orphan becomes invisible.
 *
 * This is the set the server's strand-guard uses to refuse a ProgramUserGroupMap delete
 * (BeforeSaveHandler.CheckProgramUserGroupMapDeletes). Mirror it exactly — the client uses
 * it to tell the admin up front which orders block a removal, and any drift between the two
 * puts the UI back to promising removals the server will reject.
 *
 * AwaitingRelease is a member for the same reason: a collected (Collect, Organize and Place)
 * order can sit for weeks awaiting its Earliest Placement Date, which is a long window in
 * which someone could delete the membership and strand a whole batch.
 *
 * Mirrors JobOrderStatus.UnplacedStatuses on the server (Proximity.Model/JobOrder.cs).
 */
export const UNPLACED_STATUSES: JobOrderStatusEnum[] = [
  JobOrderStatusEnum.Setup,
  JobOrderStatusEnum.AwaitingApproval,
  JobOrderStatusEnum.AwaitingRelease,
  JobOrderStatusEnum.AwaitingPlacement,
  JobOrderStatusEnum.OnHold,
];
/// </module-code>

export class JobOrder extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  // populated via API query to Uniformax - not a real navigation prop
  invoices: InvoiceHeaderDTO[] = []; // not a real navigation prop - populated manually in fixup method after retrieving from server

  isPlacedOrClosed() {
    return this.jobOrderStatusId === JobOrderStatusEnum.Placed || this.jobOrderStatusId === JobOrderStatusEnum.Closed;
  }

  /** See AT_OR_BEYOND_PLACEMENT_STATUSES above. Returns false for a null/undefined status. */
  isAtOrBeyondPlacement() {
    return AT_OR_BEYOND_PLACEMENT_STATUSES.includes(this.jobOrderStatusId);
  }

  /** See UNPLACED_STATUSES above. Returns false for a null/undefined status. */
  isUnplaced() {
    return UNPLACED_STATUSES.includes(this.jobOrderStatusId);
  }

  getTotalAmt() {
    const jodPriceInfos = this.jobOrderDetails.map((jod) => jod.getPriceInfo()) as JobOrderDetailPriceInfo[];
    const amt = _.sum(jodPriceInfos.map((x) => x.extendedPrice)) ?? 0;
    return amt;
  }

  friendlyStatusName() {
    switch (this.jobOrderStatusId) {
      case JobOrderStatusEnum.Setup:
        return 'New Order';
      case JobOrderStatusEnum.AwaitingRelease:
        return 'Collected - Awaiting Administrator Release';
      case JobOrderStatusEnum.AwaitingPlacement:
        return 'Will Be Sent For Fulfillment';
      case JobOrderStatusEnum.AwaitingApproval:
        return 'Awaiting Approval';
      case JobOrderStatusEnum.Rejected:
        return 'Approval Was Rejected';
      case JobOrderStatusEnum.Placed:
        return 'Sent to Suppliers For Fulfillment';
      case JobOrderStatusEnum.Cancelled:
        return 'Order Has Been Canceled';
      case JobOrderStatusEnum.Invalid:
        return 'Invalid - Contact Your Administrator';

      default:
        return this.jobOrderStatus.name;
    }
  }

  get po() {
    if (!this.accountPurchaseOrderId) {
      return 'None';
    }
    return this.accountPurchaseOrder.purchaseOrder;
  }

  /*   isAllowReturnRepair(): number {
    let maxReturnDays = 0;
    let maxRepairDays = 0;
    this.jobOrderDetails.forEach(i => {
      if ((i.productTypeConfigProduct.productTypeConfig?.returnPolicy) && 
          (i.productTypeConfigProduct.productTypeConfig?.returnPolicy.allowReturn)) {
        if (<number>i.productTypeConfigProduct.productTypeConfig?.returnPolicy.returnDays > maxReturnDays) {
          maxReturnDays = <number>i.productTypeConfigProduct.productTypeConfig?.returnPolicy.returnDays;
        }
      }

      if ((i.productTypeConfigProduct.productTypeConfig?.returnPolicy) && 
          (i.productTypeConfigProduct.productTypeConfig?.returnPolicy.allowRepair)) {
        if (<number>i.productTypeConfigProduct.productTypeConfig?.returnPolicy.repairDays > maxRepairDays) {
          maxRepairDays = <number>i.productTypeConfigProduct.productTypeConfig?.returnPolicy.repairDays;
        }
      }
    });
    return Math.max(maxReturnDays, maxRepairDays);
  } */
  /// </code>

  // Generated code. Do not place code below this line.
  /** Guid key */
  id!: string;
  /** Guid FK accountAdminGroup */
  accountAdminGroupId?: string;
  /** Guid FK account */
  accountId!: string;
  /** Guid FK accountProcurementCard */
  accountProcurementCardId?: string;
  /** Guid FK accountPurchaseOrder */
  accountPurchaseOrderId?: string;
  /** Guid FK accountUser */
  accountUserId?: string;
  /** Guid FK approvedByAccountAdmin */
  approvedByAccountAdminId?: string;
  /** Guid FK billingAccountAddress */
  billingAccountAddressId?: string;
  /** String */
  displayId?: string;
  /** Boolean */
  doesAccountPayFreight!: boolean;
  /** Enum FK jobOrderStatus */
  jobOrderStatusId!: JobOrderStatusEnum;
  /** Enum FK jobOrderType */
  jobOrderTypeId!: JobOrderTypeEnum;
  /** Guid FK manifest */
  manifestId?: string;
  /** DateTimeOffset */
  orderApprovalSubmittedTs?: Date;
  /** DateTimeOffset */
  orderCreatedTs!: Date;
  /** DateTimeOffset */
  orderPlacedTs?: Date;
  /** Guid FK placedByProximityUser */
  placedByProximityUserId?: string;
  /** Decimal */
  prepaymentAmt?: number;
  /** Guid FK program */
  programId!: string;
  /** Guid FK programUserGroup */
  programUserGroupId?: string;
  /** Binary */
  rowVersion!: any;
  /** Enum FK shipFrequency */
  shipFrequencyId!: ShipFrequencyEnum;
  /** Guid FK shippingAccountAddress */
  shippingAccountAddressId?: string;
  /** FK accountId */
  account!: Account;
  /** FK accountAdminGroupId */
  accountAdminGroup!: AccountAdminGroup;
  /** Inv FK jobOrderId */
  accountIssuanceUserLogs!: AccountIssuanceUserLog[];
  /** FK accountProcurementCardId */
  accountProcurementCard!: AccountProcurementCard;
  /** FK accountPurchaseOrderId */
  accountPurchaseOrder!: AccountPurchaseOrder;
  /** Inv FK jobOrderId */
  accountPurchaseOrderLogs!: AccountPurchaseOrderLog[];
  /** FK accountUserId */
  accountUser!: AccountUser;
  /** FK approvedByAccountAdminId */
  approvedByAccountAdmin!: AccountAdmin;
  /** FK billingAccountAddressId */
  billingAccountAddress!: AccountAddress;
  /** Inv FK jobOrderId */
  cancellationRequests!: CancellationRequest[];
  /** Inv FK jobOrderId */
  jobOrderBudgetLogs!: JobOrderBudgetLog[];
  /** Inv FK jobOrderId */
  jobOrderDetails!: JobOrderDetail[];
  /** Inv FK jobOrderId */
  jobOrderHistDetails!: JobOrderHistDetail[];
  /** Inv FK jobOrderId */
  jobOrderNotes!: JobOrderNote[];
  /** Inv FK jobOrderId */
  jobOrderOveragePayments!: JobOrderOveragePayment[];
  /** FK jobOrderStatusId */
  jobOrderStatus!: JobOrderStatus;
  /** FK jobOrderTypeId */
  jobOrderType!: JobOrderType;
  /** FK manifestId */
  manifest!: Manifest;
  /** FK placedByProximityUserId */
  placedByProximityUser!: ProximityUser;
  /** FK programId */
  program!: Program;
  /** Inv FK jobOrderId */
  programAllowanceUserLogs!: ProgramAllowanceUserLog[];
  /** Inv FK jobOrderId */
  programIssuanceUserLogs!: ProgramIssuanceUserLog[];
  /** FK programUserGroupId */
  programUserGroup!: ProgramUserGroup;
  /** Inv FK jobOrderId */
  returnRequests!: ReturnRequest[];
  /** FK shipFrequencyId */
  shipFrequency!: ShipFrequency;
  /** FK shippingAccountAddressId */
  shippingAccountAddress!: AccountAddress;
}

